import { useLocation } from "react-router-dom";
import SEO from "@/components/SEO";
import { metaForPath, splitLocalePath, SITE_ORIGIN } from "@/lib/seoConfig";

/**
 * SEORouter — reads the current URL, resolves per-route metadata from the
 * central config, and renders a single <SEO/> for the whole app.
 *
 * Placed once inside <BrowserRouter/> in App.js.
 *
 * Dynamic pages (journal/:slug) opt out of central metadata by returning null
 * from metaForPath — those pages render their own <SEO/> internally.
 */
export default function SEORouter() {
  const { pathname } = useLocation();
  const meta = metaForPath(pathname);

  if (!meta) return null; // dynamic page owns its own SEO

  const { locale } = splitLocalePath(pathname);
  // Canonical always points at the actual URL including locale prefix, unless
  // an explicit canonicalOverride is provided (e.g. /blog → /journal, /pre-order → /preorder).
  const canonicalPath = meta.canonicalOverride
    ? (locale === "en" ? meta.canonicalOverride : `/${locale}${meta.canonicalOverride}`)
    : pathname;

  return (
    <SEO
      title={meta.title}
      description={meta.description}
      path={canonicalPath}
      robots={meta.robots || "index,follow"}
      ogImage={meta.ogImage}
      jsonLd={meta.jsonLd}
    />
  );
}

export { SITE_ORIGIN };
